"use client";
import {
  getAndSetUserSubscriptionStatusAndIsTrialEligible,
  getMandatePausedTypeAction,
  setCookieViaAction,
  setSelectedProfileInCookie,
} from "@/actions/user.actions";
import { apiSdkInstance } from "@/apiSdk/stageApiSdk.instance";
import {
  CookiesEnum,
  cookiesValues,
  DialectsEnum,
  langDetails,
  LangEnum,
  MandatePausedTypeEnum,
  MAX_PROFILES,
  SUBSCRIPTION_STATUS,
} from "@/constants/constants";
import { Profile } from "@/types/user.types";
import {
  captureError,
  getClientSideCookieValue,
  getLangDetailsFromApiKey,
  stopGlobalPaymentPolling,
  updateUserClientId,
} from "@/utils/common.helpers";
import { isInternationalUser } from "@/utils/location.helpers";
import { windowHelpers } from "@/utils/window.helpers";
import { useRouter } from "next/navigation";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const PROFILE_MYLIST_FETCH_DELAY_MS = 100;
const MANDATE_STATUS_FETCH_DELAY_MS = 500;

export interface User {
  mobileNumber: string;
  userId: string;
}

interface UserContextType {
  user: User;
  isSubscribed: boolean;
  isLoggedIn: boolean;
  subscriptionStatus: number;
  isSubscriptionStatusLoading: boolean;
  isTrialEligibleUser: boolean;
  isMandatePaused: boolean;
  mandatePausedLoading: boolean;
  mandatePausedType: MandatePausedTypeEnum;
  isGlobalPlan: boolean;
  hasActiveAccess: boolean;
  isInternational: boolean;
  setIsInternational: (isInternational: boolean) => void;

  // my list
  myList: MyListStore;
  setMyList: (myList: MyListStore) => void;

  // profiles
  profiles: Profile[];
  profilesLoading: boolean;
  selectedProfile: Profile | null;
  canAddMoreProfiles: boolean;
  isFetchingCurrentProfile: boolean;
  setSelectedProfile: (profileId: string) => Promise<void>;
  fetchProfiles: () => Promise<Profile[] | undefined>;

  login: (newUser: User, subscriptionStatus: number) => void;
  updateSubscription: (subscrptionStatus: number, isGlobal?: boolean) => void;
  logout: () => void;

  updateUserDialect: (userDialect: string) => Promise<void>;
  updateUserLang: (userLang: string) => Promise<void>;
}

type MyListStore = {
  _id: number;
  type: string;
}[];

const defaultUserContext = {
  isSubscribed: false,
  isLoggedIn: false,
  subscriptionStatus: 0,
  isSubscriptionStatusLoading: true,
  isTrialEligibleUser: true,
  isMandatePaused: false,
  mandatePausedLoading: true,
  mandatePausedType: MandatePausedTypeEnum.NONE,
  isGlobalPlan: false,
  hasActiveAccess: false,
  isInternational: false,
  user: {
    mobileNumber: "",
    userId: "",
  },
  profiles: [],
  profilesLoading: true,
  selectedProfile: null,
  canAddMoreProfiles: true,
  isFetchingCurrentProfile: true,
  updateUserDialect: async () => {},
  updateUserLang: async () => {},
};

export const UserContext = createContext<UserContextType>({
  ...defaultUserContext,
  myList: [],
  setMyList: () => {},
  setSelectedProfile: async () => {},
  fetchProfiles: async () => [],
  login: () => {},
  updateSubscription: () => {},
  logout: () => {},
  setIsInternational: () => {},
});

export const UserProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const router = useRouter();
  const [user, setUser] = useState<User>(defaultUserContext.user);
  const [subscriptionStatus, setSubscriptionStatus] = useState<number>(
    defaultUserContext.subscriptionStatus,
  );
  const [isSubscribed, setIsSubscribed] = useState<boolean>(
    defaultUserContext.isSubscribed,
  );
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    defaultUserContext.isLoggedIn,
  );
  const [isTrialEligibleUser, setIsTrialEligibleUser] = useState<boolean>(
    defaultUserContext.isTrialEligibleUser,
  );
  const [isMandatePaused, setIsMandatePaused] = useState<boolean>(
    defaultUserContext.isMandatePaused,
  );
  const [mandatePausedLoading, setMandatePausedLoading] = useState<boolean>(
    defaultUserContext.mandatePausedLoading,
  );
  const [isGlobalPlan, setIsGlobalPlan] = useState<boolean>(
    defaultUserContext.isGlobalPlan,
  );
  const [mandatePausedType, setMandatePausedType] =
    useState<MandatePausedTypeEnum>(defaultUserContext.mandatePausedType);
  const [myList, setMyList] = useState<MyListStore>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState<boolean>(
    defaultUserContext.profilesLoading,
  );
  const [selectedProfile, _setSelectedProfile] = useState<Profile | null>(
    defaultUserContext.selectedProfile,
  );
  const [isFetchingCurrentProfile, setIsFetchingCurrentProfile] =
    useState<boolean>(true);
  const [isSubscriptionStatusLoading, setIsSubscriptionStatusLoading] =
    useState<boolean>(true);
  const [isInternational, setIsInternational] = useState<boolean>(
    isInternationalUser(),
  );

  const hasActiveAccess = useMemo(() => {
    if (!isSubscribed) return false;
    if (isGlobalPlan) return true;
    return !isInternational;
  }, [isSubscribed, isGlobalPlan, isInternational]);

  const fetchProfiles = useCallback(async () => {
    try {
      setProfilesLoading(true);
      const userProfiles = await apiSdkInstance.user.fetchUserProfiles();
      setProfiles(userProfiles);

      return userProfiles;
    } catch (error) {
      console.error("Failed to fetch user profiles:", error);
      captureError(error);
    } finally {
      setProfilesLoading(false);
    }
  }, []);

  const fetchAndSetSelectedProfile = useCallback(async () => {
    const profiles = await fetchProfiles();
    if (!profiles) {
      return;
    }

    const selectedProfileId = getClientSideCookieValue(
      cookiesValues[CookiesEnum.Profile],
    );
    const selectedProfile = profiles.find(
      (profile) => profile.profileId === selectedProfileId,
    );

    if (selectedProfile) {
      _setSelectedProfile(selectedProfile);
    }
    setIsFetchingCurrentProfile(false);
  }, [fetchProfiles]);

  useEffect(() => {
    setIsSubscribed(subscriptionStatus === 1);
  }, [subscriptionStatus]);

  useEffect(() => {
    const token = getClientSideCookieValue(cookiesValues[CookiesEnum.Token]);
    setIsLoggedIn(!!token);
    if (!token) {
      setIsSubscriptionStatusLoading(false);
      setIsFetchingCurrentProfile(false);
    } else {
      // Read cached subscription status from cookie to avoid flash of incorrect UI
      // while the API call resolves the true subscription state
      const cachedStatus = getClientSideCookieValue(
        cookiesValues[CookiesEnum.Status],
      );
      if (cachedStatus) {
        try {
          const parsedStatus = JSON.parse(cachedStatus);
          setSubscriptionStatus(parsedStatus);
          setIsSubscribed(parsedStatus === SUBSCRIPTION_STATUS.SUBSCRIBED);
        } catch {
          // Cookie value is malformed, fall through to API call
        }
      }
      // Read cached isGlobalPlan from cookie to avoid flash of LocationChangePopup
      // for international users who have a global plan
      const cachedIsGlobal = getClientSideCookieValue(
        cookiesValues[CookiesEnum.IsGlobalPlan],
      );
      if (cachedIsGlobal) {
        try {
          setIsGlobalPlan(JSON.parse(cachedIsGlobal));
        } catch {
          // Cookie value is malformed, fall through to API call
        }
      }
    }
  }, []);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const { subscriptionStatus, isTrialEligible, isGlobal } =
          await getAndSetUserSubscriptionStatusAndIsTrialEligible();
        setSubscriptionStatus(subscriptionStatus);
        setIsSubscriptionStatusLoading(false);
        setIsTrialEligibleUser(isTrialEligible);
        setIsGlobalPlan(isGlobal);
      } catch (error) {
        console.error("Failed to fetch user subscription details:", error);
        setIsSubscriptionStatusLoading(false);
      }

      const userData = getClientSideCookieValue(
        cookiesValues[CookiesEnum.UserData],
      );
      if (userData) {
        const userDataJSON = JSON.parse(decodeURIComponent(userData)) as User;
        setUser({
          mobileNumber: userDataJSON?.mobileNumber || "",
          userId: userDataJSON?.userId || "",
        });
      }
    };

    const fetchMandatePaused = async () => {
      try {
        const { isMandatePaused, mandatePausedType } =
          await getMandatePausedTypeAction();
        setIsMandatePaused(isMandatePaused);
        setMandatePausedType(mandatePausedType);
        setMandatePausedLoading(false);
      } catch (error) {
        console.error("Failed to fetch mandate paused state:", error);
        setIsMandatePaused(false);
        setMandatePausedType(MandatePausedTypeEnum.NONE);
        setMandatePausedLoading(false);
      }
    };

    async function fetchAndSetMyList() {
      const dialect = getClientSideCookieValue(
        cookiesValues[CookiesEnum.Dialect],
      );
      const lang = getClientSideCookieValue(cookiesValues[CookiesEnum.Lang]);

      const myListResponse = await apiSdkInstance.user.getMyList({
        dialect: dialect as DialectsEnum,
        lang: lang as LangEnum,
      });
      if (myListResponse && !("error" in myListResponse)) {
        setMyList(myListResponse.items);
      }
    }

    let profileMyListTimerId: ReturnType<typeof setTimeout> | null = null;
    let mandateStatusTimerId: ReturnType<typeof setTimeout> | null = null;

    if (isLoggedIn) {
      fetchUserDetails();

      profileMyListTimerId = setTimeout(() => {
        fetchAndSetSelectedProfile();
        fetchAndSetMyList();
      }, PROFILE_MYLIST_FETCH_DELAY_MS);

      mandateStatusTimerId = setTimeout(() => {
        fetchMandatePaused();
      }, MANDATE_STATUS_FETCH_DELAY_MS);
    }

    return () => {
      if (profileMyListTimerId) clearTimeout(profileMyListTimerId);
      if (mandateStatusTimerId) clearTimeout(mandateStatusTimerId);
    };
  }, [isLoggedIn, fetchProfiles]);

  const login = (newUser: User, subscriptionStatus: number) => {
    setUser(newUser);
    setIsLoggedIn(true);
    setSubscriptionStatus(subscriptionStatus);
    // Sync the user's Google Analytics client ID with their user ID in the backend
    updateUserClientId();
  };
  const updateSubscription = (
    subscrptionStatus: number,
    isGlobal?: boolean,
  ) => {
    if (isLoggedIn) {
      setUser((user) => ({
        ...user,
        subscriptionStatus: subscrptionStatus,
      }));
      setSubscriptionStatus(subscrptionStatus);

      // If isGlobal is known (e.g. Stripe/international payments), set it immediately
      // to avoid a race condition where hasActiveAccess is false while the async fetch resolves.
      // Also persist to cookie so it survives page navigations.
      if (isGlobal !== undefined) {
        setIsGlobalPlan(isGlobal);
        setCookieViaAction({
          key: cookiesValues[CookiesEnum.IsGlobalPlan],
          value: JSON.stringify(isGlobal),
        });
      }

      // Re-fetch subscription details to get the latest isGlobal status.
      // Skip overwriting isGlobalPlan if we already set it optimistically above,
      // since the backend may not have processed the Stripe webhook yet.
      getAndSetUserSubscriptionStatusAndIsTrialEligible().then(
        ({ isGlobal: fetchedIsGlobal }) => {
          if (isGlobal === undefined) {
            setIsGlobalPlan(fetchedIsGlobal);
          }
        },
      );
    } else {
      throw captureError(
        new Error(
          "Trying to update subscription status without login. Login before updating subscription status",
        ),
      );
    }
  };

  const logout = () => {
    stopGlobalPaymentPolling();
    if (isLoggedIn) {
      setUser(defaultUserContext.user);
      setIsLoggedIn(defaultUserContext.isLoggedIn);
      setSubscriptionStatus(defaultUserContext.subscriptionStatus);
      setIsTrialEligibleUser(defaultUserContext.isTrialEligibleUser);
      setProfiles(defaultUserContext.profiles);
      setProfilesLoading(defaultUserContext.profilesLoading);
      _setSelectedProfile(defaultUserContext.selectedProfile);
      setIsMandatePaused(defaultUserContext.isMandatePaused);
      setMandatePausedLoading(defaultUserContext.mandatePausedLoading);
      setMandatePausedType(defaultUserContext.mandatePausedType);
      setIsGlobalPlan(defaultUserContext.isGlobalPlan);
    } else {
      throw captureError(new Error("Trying to logout before logging in."));
    }
  };

  const canAddMoreProfiles = useMemo(() => {
    return profiles.length < MAX_PROFILES;
  }, [profiles.length]);

  const setSelectedProfile = useCallback(async (profileId: string) => {
    const response = await apiSdkInstance.user.getSwitchProfilesUrl(profileId);

    if (!response.token || !response.userProfile) {
      return;
    }

    const { token: newToken, userProfile: newUserProfile } = response;
    await setCookieViaAction({
      key: cookiesValues[CookiesEnum.Token],
      value: newToken,
    });

    // Clear cached data when switching profiles
    setMyList([]);

    _setSelectedProfile(newUserProfile);

    windowHelpers.startProfileLoader();
    const profileIdFromCookie = getClientSideCookieValue(
      cookiesValues[CookiesEnum.Profile],
    );
    const userLang =
      getLangDetailsFromApiKey(newUserProfile.language)?.langCode || "";

    const availableDialects = await windowHelpers.getAvailableDialects();
    const userDialect =
      availableDialects?.find(
        (dialect) =>
          dialect.dialectAbbreviation === newUserProfile.contentCulture,
      )?.urlValue || "";

    if (profileIdFromCookie !== newUserProfile.profileId) {
      await setSelectedProfileInCookie(
        newUserProfile.profileId,
        userLang,
        userDialect,
      );
    }

    router.replace(`/${userLang}/${userDialect}`);
  }, []);

  const updateUserProfile = async (updates: {
    contentCulture?: string;
    language?: string;
  }) => {
    if (!selectedProfile) return;

    await apiSdkInstance.user.createProfile({
      isEditFlow: true,
      avatar: selectedProfile.avatar,
      contentCulture: updates.contentCulture ?? selectedProfile.contentCulture,
      language: updates.language ?? selectedProfile.language,
      profileName: selectedProfile.displayName,
      currentProfile: selectedProfile,
    });
    fetchAndSetSelectedProfile();
  };

  const updateUserDialect = async (userDialect: string) => {
    if (!userDialect || !selectedProfile) return;

    const newContentCulture = (
      await windowHelpers.getAvailableDialects()
    )?.find((dialect) => dialect.urlValue === userDialect)?.dialectAbbreviation;

    if (!newContentCulture) return;

    await updateUserProfile({ contentCulture: newContentCulture });
  };

  const updateUserLang = async (userLang: string) => {
    if (!userLang || !selectedProfile) return;

    const langKey = langDetails[userLang as keyof typeof langDetails]?.apiKey;
    if (!langKey) return;

    await updateUserProfile({ language: langKey });
  };

  return (
    <UserContext.Provider
      value={{
        isSubscribed,
        subscriptionStatus,
        isSubscriptionStatusLoading,
        isLoggedIn,
        isTrialEligibleUser,
        isMandatePaused,
        mandatePausedLoading,
        mandatePausedType,
        isGlobalPlan,
        hasActiveAccess,
        isInternational,
        setIsInternational,
        user,
        myList,
        profiles,
        profilesLoading,
        selectedProfile,
        canAddMoreProfiles,
        isFetchingCurrentProfile,
        updateUserDialect,
        updateUserLang,
        setMyList,
        setSelectedProfile,
        fetchProfiles,
        login,
        updateSubscription,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
